import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { StaffProfileService } from '../shared/staff-profile/staff-profile.service';

/**
 * /complete-profile: staff enter their date of birth once before using the
 * Board (ProfileCompletionGuard sends them here). Chrome-less, like /locked.
 */
@Component({
  selector: 'ngx-complete-profile',
  templateUrl: './complete-profile.component.html',
  styleUrls: ['./complete-profile.component.scss']
})
export class CompleteProfileComponent implements OnInit {
  loading = true;
  saving = false;
  done = false;
  firstName = '';
  confetti: { left: number; delay: number; color: string; rotate: number }[] = [];
  dateOfBirth = '';
  error = '';
  /** Allowed range for the date input (16 to 100 years ago, same as the server). */
  minDate = '';
  maxDate = '';

  constructor(private staffProfile: StaffProfileService, private router: Router) {}

  ngOnInit(): void {
    // The chrome-less route doesn't mount <nb-layout>, which normally removes
    // the index.html splash spinner. Remove it so it doesn't cover the card.
    document.getElementById('nb-global-spinner')?.remove();

    try {
      this.firstName = (JSON.parse(localStorage.getItem('user') || 'null')?.firstName || '').trim();
    } catch {
      this.firstName = '';
    }

    const today = new Date();
    const ymd = (y: number) =>
      `${y}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    this.maxDate = ymd(today.getFullYear() - 16);
    this.minDate = ymd(today.getFullYear() - 100);

    this.staffProfile.getDateOfBirthStatus().subscribe({
      next: (status) => {
        // Nothing to do here (already entered, or not required): back to the Board.
        if (!status?.required) {
          this.router.navigate(['/pages/home']);
          return;
        }
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
      // No answer at all (no connection): show the form rather than "One moment…".
      complete: () => {
        this.loading = false;
      }
    });
  }

  save(): void {
    this.error = '';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(this.dateOfBirth || '')) {
      this.error = 'Please enter your date of birth.';
      return;
    }
    this.saving = true;
    let answered = false;
    this.staffProfile.saveDateOfBirth(this.dateOfBirth).subscribe({
      next: () => {
        answered = true;
        this.saving = false;
        this.done = true;
        this.confetti = Array.from({ length: 28 }, (_, i) => ({
          left: (i * 37) % 100,
          delay: (i * 53) % 500,
          color: ['#c9a227', '#f5c542', '#13234b', '#f5e7b5'][i % 4],
          rotate: (i * 47) % 360
        }));
        const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        setTimeout(() => this.router.navigate(['/pages/home']), reduced ? 600 : 1700);
      },
      error: (err) => {
        answered = true;
        this.saving = false;
        // The error interceptor passes on the response body ({ statusCode, message }).
        const message = err?.message ?? err?.error?.message;
        this.error = typeof message === 'string' && message ? message : 'Could not save. Please try again.';
      },
      complete: () => {
        // Ended with no answer (no connection): let them try again.
        if (!answered) {
          this.saving = false;
          this.error = 'Could not reach the server. Please try again.';
        }
      }
    });
  }

  /** A complete date inside the allowed range (the server checks again). */
  get isValid(): boolean {
    const v = this.dateOfBirth || '';
    return /^\d{4}-\d{2}-\d{2}$/.test(v) && v >= this.minDate && v <= this.maxDate;
  }

  /** e.g. "15 March" (day and month only). */
  get prettyDate(): string {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(this.dateOfBirth || '');
    if (!m) return '';
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August',
      'September', 'October', 'November', 'December'];
    return `${Number(m[3])} ${months[Number(m[2]) - 1]}`;
  }

  logout(): void {
    localStorage.removeItem('user');
    localStorage.removeItem('auth_app_token');
    this.router.navigateByUrl('/auth/login');
  }
}
